import React, { useState } from "react";
import { Search, ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from "lucide-react";

function DexKorOverview({
  setView,
  setupDetails,
  setIsOpen,
  isExpanded,
  setIsExpanded,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const articles = [
    {
      id: "what-is-dexkor",
      title: "What is DexKor?",
      description: "How DexKor works, what it can do for your business and what makes it different.",
    },
    {
      id: "setup-workspace",
      title: "Set up your workspace",
      description: "The initial setup steps DexKor suggests for a new project, team roles, and settings.",
    },
    {
      id: "install-sdk",
      title: "Install the DexKor SDK",
      description: "Platforms DexKor supports, where to find your SDK key, and setup guides.",
    },
    {
      id: "ai-coding",
      title: "Let an AI coding agent install DexKor",
      description: "Add DexKor's agent skills to your project so Claude Code, Cursor, or Codex can configure it.",
    },
    {
      id: "identify-users",
      title: "Identify your logged-in users",
      description: "Tell DexKor who is using your product, which data you can send, and link people.",
    },
    {
      id: "plans-pricing",
      title: "Plans & pricing",
      description: "DexKor plans, seats, transparent billing options, and enterprise tiers.",
    },
  ];

  const filtered = articles.filter(
    (a) =>
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full flex flex-col h-full bg-white overflow-hidden">
      {/* --- Gleap White Top Header (54px) --- */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
        <button
          onClick={() => setView("Help")}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back to help"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        <div className="font-bold text-slate-900 text-sm tracking-tight text-center truncate px-2 flex-1">
          DexKor Overview
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          {setIsExpanded && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              ) : (
                <Maximize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
              else setView("Help");
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* --- Scrollable Body --- */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-4 dexkor-scrollbar">
        {/* Search Bar */}
        <div className="bg-slate-100/80 hover:bg-slate-100 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 transition-colors border border-transparent focus-within:border-slate-300 focus-within:bg-white shadow-none">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search in this collection..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-xs text-slate-800 placeholder-slate-400 border-none outline-none bg-transparent"
          />
        </div>

        {/* Collection Header Title & Description */}
        <div className="pt-1">
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            DexKor Overview
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Set up your workspace, install the SDK, and get your team working in DexKor.
          </p>
        </div>

        {/* Articles Card Container (Gleap rounded card style) */}
        <div className="rounded-2xl border border-slate-200/90 bg-white overflow-hidden shadow-sm divide-y divide-slate-100">
          {filtered.map((item) => (
            <div
              key={item.id}
              onClick={() => setView("DexKor")}
              className="group flex items-center justify-between p-3.5 hover:bg-slate-50 cursor-pointer transition-colors active:bg-slate-100/80"
            >
              <div className="flex-1 pr-3 min-w-0">
                <h4 className="text-xs font-semibold text-slate-900 group-hover:text-black">
                  {item.title}
                </h4>
                <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">
                  {item.description}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all" />
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-400">
              No matching articles found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DexKorOverview;
